import {
  Directive,
  ElementRef,
  ViewContainerRef,
  NgZone,
  ChangeDetectorRef,
  Inject,
  Optional,
  Host,
  HostBinding,
  HostListener,
  Input,
  forwardRef,
} from '@angular/core';
import {
  MatAutocompleteTrigger,
  MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
  MatAutocomplete,
} from '@angular/material/autocomplete';
import { MatFormField } from '@angular/material/form-field';
import { Overlay, ViewportRuler } from '@angular/cdk/overlay';
import { Directionality } from '@angular/cdk/bidi';
import { DOCUMENT } from '@angular/common';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: `input[appAutocomplete], textarea[appAutocomplete]`,
  exportAs: 'appAutocompleteTrigger',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppAutocompleteTriggerDirective),
      multi: true,
    },
  ],
})
export class AppAutocompleteTriggerDirective extends MatAutocompleteTrigger {
  @HostBinding('attr.autocomplete') attrAuto = 'off';
  @HostBinding('attr.role') attrRole = this.autocompleteDisabled ? null : 'combobox';
  @HostBinding('attr.aria-autocomplete') attrAriaAuto = this.autocompleteDisabled ? null : 'list';
  @HostBinding('attr.aria-activedescendant')
  attrAriaActiveD = this.activeOption && this.activeOption.id;
  @HostBinding('attr.aria-expanded')
  attrAriaExp = this.autocompleteDisabled ? null : this.panelOpen.toString();
  @HostBinding('attr.aria-owns')
  attrAriaOwns = this.autocompleteDisabled ? null : this.autocomplete && this.autocomplete.id;
  private previousInputValue: string;

  constructor(
    _element: ElementRef,
    _overlay: Overlay,
    _viewContainerRef: ViewContainerRef,
    _zone: NgZone,
    _changeDetectorRef: ChangeDetectorRef,
    @Inject(MAT_AUTOCOMPLETE_SCROLL_STRATEGY) _scrollStrategy,
    @Optional() _dir: Directionality,
    @Optional()
    @Host()
    _formField: MatFormField,
    @Optional()
    @Inject(DOCUMENT)
    _document: // tslint:disable-next-line
    any,
    _viewportRuler?: ViewportRuler
  ) {
    super(
      _element,
      _overlay,
      _viewContainerRef,
      _zone,
      _changeDetectorRef,
      _scrollStrategy,
      _dir,
      _formField,
      _document,
      _viewportRuler
    );
  }

  // tslint:disable-next-line:no-input-rename
  @Input('appAutocomplete') autocomplete: MatAutocomplete;

  @Input() closeOnSelect = true;

  @HostListener('focusin')
  onFocusIn() {
    this._handleFocus();
  }

  @HostListener('blur')
  onBlur() {
    this._onTouched();
  }

  @HostListener('input', ['$event'])
  onInput($event) {
    this._handleInput($event);
  }

  @HostListener('keydown', ['$event'])
  onKeydown($event) {
    this._handleKeydown($event);
  }

  _handleInput(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    this.previousInputValue = target.value;
    super._handleInput(event);
  }

  closePanel(): void {
    if (this['_element'].nativeElement.value === '[object Object]' && !this.closeOnSelect) {
      this['_subscribeToClosingActions']();
      this.writeValue(this.previousInputValue);
      this._onChange(this.previousInputValue);
    } else {
      this.previousInputValue = null;
      super.closePanel();
    }
  }
}
